import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Phone } from "lucide-react";
import { Switch, FormControlLabel, Button, Tabs, Tab } from "@mui/material";
import confetti from "canvas-confetti";

const businessPlans = [
  {
    name: "STARTER",
    price: "299",
    yearlyPrice: "239",
    period: "per month",
    features: [
      "Up to 10 projects",
      "Basic analytics",
      "48-hour support response time",
      "Limited API access",
      "Community support",
    ],
    description: "Perfect for small businesses and startups",
    buttonText: "Start Free Trial",
    href: "/sign-up",
    isPopular: false,
  },
  {
    name: "PROFESSIONAL",
    price: "499",
    yearlyPrice: "399",
    period: "per month",
    features: [
      "Unlimited projects",
      "Advanced analytics",
      "24-hour support response time",
      "Full API access",
      "Priority support",
      "Team collaboration",
      "Custom integrations",
    ],
    description: "Ideal for growing businesses and organizations",
    buttonText: "Start Free Trial",
    href: "/sign-up",
    isPopular: true,
  },
  {
    name: "ENTERPRISE",
    price: "Call Us",
    yearlyPrice: "Call Us",
    period: "",
    features: [
      "Everything in Professional",
      "Custom solutions",
      "Dedicated account manager",
      "1-hour support response time",
      "SSO Authentication",
      "Advanced security",
      "Custom contracts",
      "SLA agreement",
    ],
    description: "For large organizations with specific needs",
    buttonText: "Contact Sales",
    href: "/contact",
    isPopular: false,
    isCallUs: true,
  },
];

const recruiterPlans = [
  {
    name: "STARTER",
    price: "99",
    yearlyPrice: "79",
    period: "per month",
    features: [
      "Up to 5 job postings",
      "Basic candidate search",
      "48-hour support response time",
      "Limited API access",
      "Community support",
    ],
    description: "Perfect for independent recruiters",
    buttonText: "Start Free Trial",
    href: "/sign-up",
    isPopular: false,
  },
  {
    name: "PROFESSIONAL",
    price: "199",
    yearlyPrice: "159",
    period: "per month",
    features: [
      "Unlimited job postings",
      "Advanced candidate search",
      "24-hour support response time",
      "Full API access",
      "Priority support",
      "Team collaboration",
      "Custom integrations",
    ],
    description: "Ideal for recruiting agencies and teams",
    buttonText: "Start Free Trial",
    href: "/sign-up",
    isPopular: true,
  },
  {
    name: "ENTERPRISE",
    price: "Call Us",
    yearlyPrice: "Call Us",
    period: "",
    features: [
      "Everything in Professional",
      "Custom solutions",
      "Dedicated account manager",
      "1-hour support response time",
      "SSO Authentication",
      "Advanced security",
      "Custom contracts",
      "SLA agreement",
    ],
    description: "For large recruiting organizations",
    buttonText: "Contact Sales",
    href: "/contact",
    isPopular: false,
    isCallUs: true,
  },
];

const Pricing = ({
  title = "Simple, Flexible Pricing",
  description = "Choose the plan that works for you\nAll plans include access to our platform, lead generation tools, and dedicated support.",
}) => {
  const [isMonthly, setIsMonthly] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const isDesktop =
    typeof window !== "undefined" &&
    window.matchMedia("(min-width: 768px)").matches;

  // const handleToggle = (event) => {
  //   setIsMonthly(!event.target.checked);
  //   if (event.target.checked) {
  //     const switchElement = event.target;
  //     const rect = switchElement.getBoundingClientRect();
  //     const x = rect.left + rect.width / 2;
  //     const y = rect.top + rect.height / 2;

  //     confetti({
  //       particleCount: 50,
  //       spread: 60,
  //       origin: {
  //         x: x / window.innerWidth,
  //         y: y / window.innerHeight,
  //       },
  //       colors: ["#2563eb", "#3b82f6", "#60a5fa", "#93c5fd"],
  //       ticks: 200,
  //       gravity: 1.2,
  //       decay: 0.94,
  //       startVelocity: 30,
  //       shapes: ["circle"],
  //     });
  //   }
  // };

  const currentPlans = tabValue === 0 ? businessPlans : recruiterPlans;

  return (
    <div className="container py-20">
      <div className="text-center space-y-4 mb-8">
        <h2 className="text-[#4472c6] text-4xl font-bold tracking-tight sm:text-5xl">
          {title}
        </h2>
        <p className="text-slate-600 text-lg whitespace-pre-line">
          {description}
        </p>
      </div>

      <div className="flex justify-center mb-10">
        <div className=" bg-[#97ccff] rounded-xl inline-flex ">
          <button
            onClick={() => setTabValue(0)}
            className={`px-8 py-3 rounded-lg font-large text-lg transition-all duration-300 ${
              tabValue === 0
                ? "bg-white text-blue-600 transform -translate-y-0.5"
                : "text-gray-600 hover:text-gray-800"
            }`}
            aria-current={tabValue === 0 ? "page" : undefined}
          >
            Business Plan
          </button>
          <button
            onClick={() => setTabValue(1)}
            className={`px-8 py-3 rounded-lg font-large text-lg transition-all duration-300 ${
              tabValue === 1
                ? "bg-white text-blue-600 shadow-md transform -translate-y-0.5"
                : "text-gray-600 hover:text-gray-800"
            }`}
            aria-current={tabValue === 1 ? "page" : undefined}
          >
            Recruiter Plan
          </button>
        </div>
      </div>

      {/* <div className="flex justify-center mb-8">
        <FormControlLabel
          control={
            <Switch
              checked={!isMonthly}
              onChange={handleToggle}
              color="primary"
            />
          }
          label={
            <span className="ml-2 font-semibold">
              Annual billing <span className="text-blue-600">(Save 20%)</span>
            </span>
          }
        />
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {currentPlans.map((plan, index) => (
          <motion.div
            key={index}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              delay: index * 0.1,
            }}
            whileHover={plan.isPopular ? { scale: 1.03 } : { scale: 1.01 }}
            className={`
              rounded-2xl border p-6 bg-white text-center flex flex-col relative
              ${
                plan.isPopular
                  ? "border-blue-500 border-2 shadow-lg"
                  : "border-gray-200 shadow"
              }
            `}
          >
            {plan.isPopular && (
              <div className="absolute top-0 right-0 bg-blue-500 py-0.5 px-2 rounded-bl-xl rounded-tr-xl flex items-center">
                <Star className="text-white h-4 w-4 fill-current" />
                <span className="text-white ml-1 font-sans font-semibold">
                  Popular
                </span>
              </div>
            )}
            <div className="flex-1 flex flex-col">
              <p className="text-base font-semibold text-gray-600">
                {plan.name}
              </p>
              <div className="mt-6 flex items-center justify-center gap-x-2">
                {plan.isCallUs ? (
                  <span className="text-2xl font-bold tracking-tight text-blue-600">
                    {plan.price}
                  </span>
                ) : (
                  <>
                    <span className="text-sm font-semibold">$</span>
                    <span className="text-3xl font-bold tracking-tight">
                      {isMonthly ? plan.price : plan.yearlyPrice}
                    </span>
                  </>
                )}
                {!plan.isCallUs && plan.period && (
                  <span className="text-sm font-semibold leading-6 tracking-wide text-gray-500">
                    / {plan.period}
                  </span>
                )}
              </div>

              {!plan.isCallUs && (
                <p className="text-xs leading-5 text-gray-500">
                  {isMonthly ? "billed monthly" : "billed annually"}
                </p>
              )}

              <ul className="mt-5 gap-2 flex flex-col">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                    <span className="text-left text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <hr className="w-full my-4" />

              {plan.isCallUs ? (
                <Button
                  href={plan.href}
                  variant="outlined"
                  color="primary"
                  className="w-full text-lg font-semibold tracking-tighter py-2 px-4 transform transition-all duration-300 hover:scale-105"
                  startIcon={<Phone className="h-4 w-4" />}
                >
                  {plan.buttonText}
                </Button>
              ) : (
                <Button
                  href={plan.href}
                  variant={plan.isPopular ? "contained" : "outlined"}
                  color="primary"
                  className="w-full text-lg font-semibold tracking-tighter py-2 px-4 transform transition-all duration-300 hover:scale-105"
                >
                  {plan.buttonText}
                </Button>
              )}
              <p className="mt-6 text-xs leading-5 text-gray-500">
                {plan.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
      {/* <div className="mt-16 bg-blue-50 rounded-2xl p-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-blue-700 mb-4">
            Try Before You Buy
          </h3>
          <p className="text-blue-600 mb-6">
            Start with a 14-day free trial. No credit card required.
          </p>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className="bg-blue-600 hover:bg-blue-700 py-3 px-8 text-lg font-semibold"
          >
            Start Your Free Trial
          </Button>
          <p className="mt-4 text-sm text-blue-500">
            Cancel anytime. No strings attached.
          </p>
        </div>
      </div> */}
    </div>
  );
};

export default Pricing;
